import React from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
} from 'recharts';
import type { MonthlyRevenueRecord } from '../../../hooks/useMonthlyRevenue';
import { fmtUSD, fmtMonth, trendDotColor } from '../../../utils/monthlyRevenueUtils';

interface RevenueTrendChartProps {
  records: MonthlyRevenueRecord[];
}

interface ChartEntry extends MonthlyRevenueRecord {
  label: string;
  dotColor: string;
}

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: string;
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d: ChartEntry = payload[0].payload;

  if (!d || d.invoice_month == null) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[200px]">
      <p className="text-[11px] font-bold text-slate-700 mb-2">{fmtMonth(d.invoice_month)}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Revenue</span>
          <span className="text-[12px] font-bold text-slate-800 tabular-nums">{fmtUSD(d.global_invoiced_usd ?? 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">6M Avg</span>
          <span className="text-[12px] font-semibold text-violet-700 tabular-nums">{fmtUSD(d.six_month_avg ?? 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">MoM</span>
          <span className={`text-[12px] font-semibold tabular-nums ${(d.mom_change_percent ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {(d.mom_change_percent ?? 0) >= 0 ? '+' : ''}{(d.mom_change_percent ?? 0).toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Invoices</span>
          <span className="text-[12px] font-semibold text-slate-700 tabular-nums">{Math.round(d.global_invoice_count ?? 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Position</span>
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
            d.trend_position === 'ABOVE_TREND' ? 'bg-emerald-50 text-emerald-700' :
            d.trend_position === 'BELOW_TREND' ? 'bg-red-50 text-red-700' :
            'bg-blue-50 text-blue-700'
          }`}>
            {(d.trend_position ?? 'AT_TREND').replace(/_/g, ' ')}
          </span>
        </div>
      </div>
    </div>
  );
};

export const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ records }) => {
  const data: ChartEntry[] = records.map(r => ({
    ...r,
    label: fmtMonth(r.invoice_month),
    dotColor: trendDotColor(r.trend_position),
  }));

  const maxVal = Math.max(...data.map(d => d.global_invoiced_usd));
  const yDomain = [0, Math.ceil(maxVal * 1.15 / 100000) * 100000];

  const tickFormatter = (v: number) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
          Revenue Trend
        </span>
        <span className="text-[10px] text-slate-400 ml-auto">
          Dots: green=Above trend · red=Below trend · blue=At trend
        </span>
      </div>

      <div className="p-4">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <defs>
              <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#64748B' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={tickFormatter}
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={56}
              domain={yDomain}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#E2E8F0', strokeWidth: 1 }} />

            {/* Area for revenue */}
            <Area
              type="monotone"
              dataKey="global_invoiced_usd"
              stroke="#3B82F6"
              strokeWidth={2}
              fill="url(#revenueGrad)"
              dot={false}
              activeDot={false}
              name="Revenue"
            />

            {/* 6M moving average line */}
            <Line
              type="monotone"
              dataKey="six_month_avg"
              stroke="#8B5CF6"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              activeDot={false}
              name="6M Avg"
            />

            {/* Trend-position dots via Scatter */}
            <Scatter
              dataKey="global_invoiced_usd"
              fill="#3B82F6"
              shape={(props: any) => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null) {
                  return <g />;
                }
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={trendDotColor(payload.trend_position)}
                    stroke="#fff"
                    strokeWidth={1.5}
                  />
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 justify-center">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-3 h-0.5 bg-blue-500 rounded" />
            <span>Monthly Revenue</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-3 h-0.5 bg-violet-500 rounded border-dashed" style={{ borderTop: '1.5px dashed #8B5CF6', background: 'transparent' }} />
            <span>6M Moving Avg</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span>Above Trend</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span>Below Trend</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span>At Trend</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueTrendChart;
